/**
 * FeeWarning — prominent roster-move fee callout for confirm dialogs.
 *
 * 2026-09-03 (owner request) — "This move will cost ₣4,920 — 30% of your
 * cash" shown before any PAID roster move (add / drop / market draft).
 * Renders nothing for free moves or pre-finalize teams. Amber below 10%,
 * red at 10%+ (HIGH_FEE_RATE). Mobile mirror: ConfirmDialog `warning` prop
 * fed by lib/rosterMoveMath.feeWarning().
 */
import { AlertTriangle, Gift } from 'lucide-react';
import { describeMoveFee, formatFee, freeFillLine } from '../lib/rosterMoveMath';

export default function FeeWarning({ team, className = '' }) {
  const f = describeMoveFee(team);
  // 2026-09-08 — Wildcard live: say so instead of silently showing no fee.
  if (f.wildcard) {
    return (
      <div
        className={`mt-3 flex items-start gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-2 text-left text-xs ${className}`}
        data-testid="fee-warning-wildcard"
      >
        <span className="text-sm leading-none">🃏</span>
        <p className="font-semibold">Wildcard active — this move is free and doesn't count toward your weekly ladder.</p>
      </div>
    );
  }
  // 2026-10-12 — free fill (slot left open at Finalize): price only, no fee.
  if (f.freeFill) {
    return (
      <div
        className={`mt-3 flex items-start gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-2 text-left text-xs ${className}`}
        data-testid="fee-warning-free-fill"
        data-free-fills={f.freeFills}
      >
        <Gift className="h-4 w-4 shrink-0 mt-0.5" />
        <p className="font-semibold">{freeFillLine(team)}</p>
      </div>
    );
  }
  if (f.free) return null;
  const tone = f.high
    ? 'border-destructive/40 bg-destructive/10 text-destructive'
    : 'border-yellow-500/40 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
  return (
    <div
      className={`mt-3 flex items-start gap-2 rounded-xl border px-3 py-2 text-left text-xs ${tone} ${className}`}
      data-testid="fee-warning"
      data-fee-rate={f.rate}
      data-fee-high={f.high ? '1' : '0'}
    >
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="font-semibold" data-testid="fee-warning-line">
          This move will cost ₣{formatFee(f.fee)} — {f.pct}% of your cash
        </p>
        <p className="opacity-80">
          Roster move #{f.moveNo} this week · fees escalate each move and reset next gameweek.
        </p>
      </div>
    </div>
  );
}
